import {
  Box,
  Button,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { userDetailsState } from 'states';
import { useRecoilValue } from 'recoil';
import { SGModal } from '../SGModal';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import useIsMobile from 'hooks/useIsMobile';

interface UpsellModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  title: string;
  subtitle: string;
  items: string[];
}

export const UpsellModal = ({
  open,
  setOpen,
  title,
  subtitle,
  items,
}: UpsellModalProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const userDetails = useRecoilValue(userDetailsState);
  const action = userDetails != null ? 'Upgrade' : 'Subscribe';
  const isMobile = useIsMobile();

  const customActions = [
    <Button
      key={action}
      variant="contained"
      sx={{
        textTransform: 'uppercase',
        fontSize: '16px',
        fontWeight: 'bold',
        py: '10px',
        px: '32px',
        borderRadius: '8px',
        boxShadow: theme.palette.shadows.default,
      }}
      onClick={() =>
        window.open('https://spotgamma.com/subscribe-to-spotgamma/', '_blank')
      }
    >
      {`${action} Now`}
    </Button>,
    userDetails == null && (
      <Button
        key="login"
        variant="outlined"
        sx={{
          textTransform: 'uppercase',
          fontSize: '14px',
          py: '8px',
          px: '24px',
          borderRadius: '8px',
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
          ':hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            borderColor: theme.palette.primary.main,
          },
        }}
        onClick={() => navigate('/login')}
      >
        Login
      </Button>
    ),
  ];

  return (
    <SGModal
      open={open}
      onClose={() => setOpen(false)}
      customActions={customActions}
    >
      <Box
        sx={{
          padding: isMobile ? '1.2em' : '2em',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          textAlign: 'center',
          gap: '16px',
        }}
      >
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontSize: '16px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          {`${action} to Unlock`}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            color: theme.palette.text.primary,
            fontSize: isMobile ? '22px' : '28px',
            fontWeight: 700,
            textTransform: 'uppercase',
            display: 'block',
            mb: 1,
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            paddingBottom: '1em',
            fontSize: '16px',
            width: '100%',
          }}
        >
          <Typography
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '18px',
              fontWeight: 500,
              mb: 3,
            }}
          >
            {subtitle}
          </Typography>
          <Stack spacing={2} sx={{ mt: 3 }}>
            {items.map((item, i) => (
              <ListItem
                key={i}
                sx={{
                  padding: 0,
                  display: 'flex',
                  alignItems: 'flex-start',
                }}
              >
                <ListItemIcon sx={{ minWidth: '36px' }}>
                  <CheckCircleIcon
                    color="primary"
                    sx={{
                      fontSize: 24,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={item}
                  sx={{
                    m: 0,
                    '& .MuiTypography-root': {
                      color: theme.palette.text.primary,
                      fontSize: '16px',
                      lineHeight: '24px',
                      fontWeight: 400,
                      textAlign: 'left',
                    },
                  }}
                />
              </ListItem>
            ))}
          </Stack>
        </Box>
        <Box
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            borderRadius: '8px',
            p: 2,
            mt: 1,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: theme.palette.primary.main,
            }}
          >
            Unlock these premium features today and take your trading to the
            next level!
          </Typography>
        </Box>
      </Box>
    </SGModal>
  );
};
